import React, { useState } from 'react';
import {
	SimpleForm,
	SaveButton,
	Toolbar,
	SelectInput,
	required,
	TextInput,
	SelectArrayInput,
} from 'react-admin';
import { Alert, Button } from '@mui/material';
import { Box, Dialog, DialogTitle } from '@mui/material';
import {
	CONNECTOR_PROCESS_RESOLUTION_BY_CUSTOMER,
	CONNECTOR_PROCESS_RESOLUTION_IN_PROGRESS,
	IConnectorAccountProcess,
	resolutionStatusLst,
	sendResolution,
} from '../../../Services/connectorAccountProcess';
import { useWatch } from 'react-hook-form';

interface ResolutionModalProps {
	handleClose: () => void;
	handleSuccess: () => void;
	open: boolean;
	process?: IConnectorAccountProcess | null;
}

export const ResolutionModal: React.FC<ResolutionModalProps> = ({
	open,
	handleClose,
	handleSuccess,
	process,
}) => {
	const [loading, setLoading] = useState(false);

	const onHandleClose = () => {
		handleClose();
	};

	const onHandleSave = async (data: any) => {
		if (!process) return;
		setLoading(true);
		try {
			await sendResolution(process?.id, {
				resolutionStatus: data.resolutionStatus,
				resolutionMessage: data.resolutionMessage,
				resolutionThemes: data.resolutionThemes,
				issueJira: data.issueJira,
			});
			handleSuccess();
		} catch (e: any) {
			console.error(e);
			alert(e.message);
		}
		setLoading(false);
	};

	const CustomForm = () => {
		const resolutionStatus = useWatch({ name: 'resolutionStatus' });

		return (
			<>
				<Box mb={2} width="100%">
					<SelectInput
						source="resolutionStatus"
						label="Statut de résolution"
						choices={resolutionStatusLst}
						validate={required()}
						fullWidth
					/>
				</Box>

				{(resolutionStatus === CONNECTOR_PROCESS_RESOLUTION_BY_CUSTOMER ||
					resolutionStatus === CONNECTOR_PROCESS_RESOLUTION_IN_PROGRESS) && (
					<>
						<Alert severity="warning" sx={{ mb: 2 }}>
							Attention, le message sera envoyé à l'IA. Les prochains process
							ayant une erreur similaire seront résolus automatiquement.
						</Alert>
						<SelectArrayInput
							label="Thèmes de la résolution"
							source="resolutionThemes"
							parse={(value: string[] | null) => (value ? value.join(',') : '')}
							format={(value: string | null) =>
								value
									? value
											.split(',')
											.map((v) => v.trim())
											.filter(Boolean)
									: []
							}
							choices={[
								{ id: 'datawarehouse', name: 'Datawarehouse' },
								{ id: 'auth', name: 'Authentification' },
								{ id: 'credentials', name: 'Credentials' },
								{ id: 'rights', name: 'Droits sur l’api' },
								{ id: 'network', name: 'Problème réseau' },
								{ id: 'api', name: 'Problème API' },
								{ id: 'quota', name: 'Quota dépassé' },
								{ id: 'configuration', name: 'Problème de configuration' },
								{ id: 'code', name: 'Erreur de code' },
							]}
							fullWidth
						/>
					</>
				)}

				{resolutionStatus === CONNECTOR_PROCESS_RESOLUTION_IN_PROGRESS && (
					<>
						<TextInput label="Issue jira" source="issueJira" fullWidth />
					</>
				)}

				<TextInput
					label="Message de résolution"
					source="resolutionMessage"
					multiline
					fullWidth
				/>
			</>
		);
	};

	return (
		<Dialog open={open} onClose={onHandleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Statut de résolution</DialogTitle>

			<SimpleForm
				onSubmit={onHandleSave} // Utilisation de handleSave pour soumettre le formulaire
				defaultValues={{
					resolutionStatus: process?.resolutionStatus ?? '',
					resolutionMessage: process?.resolutionMessage ?? '',
					resolutionThemes: process?.resolutionThemes ?? '',
				}}
				toolbar={
					<Toolbar style={{ justifyContent: 'space-between' }}>
						<SaveButton label="Save" />
						<Button onClick={handleClose} color="secondary" disabled={loading}>
							Cancel
						</Button>
					</Toolbar>
				}
			>
				<CustomForm />
			</SimpleForm>
		</Dialog>
	);
};
